/**
 * Full-page artifact viewer (the /artifacts/:artifactId route), which is also
 * the artifact's share permalink.
 *
 * With chat available the artifact becomes a surface in the chat workspace,
 * so the same page can carry a conversation about what it is showing. The
 * rail starts collapsed to its shortcut bar: arriving here from the gallery
 * or a shared link means you came to read, and chat is an offer.
 *
 * Without chat it is the bare viewer shell, plus a back button to the gallery.
 */

import { useCallback, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router';
import { ArrowLeft } from 'lucide-react';

import {
  AgentChatProvider,
  buildArtifactSurfaceContext,
  type SurfaceContext,
} from '@/components/agent-chat';
import { ChatWorkspace } from '@/components/chat-workspace/chat-workspace';
import { useSurfaceChatHost } from '@/components/chat-workspace/use-surface-chat-host';
import { useWorkspaceSurface } from '@/components/chat-workspace/workspace-url';
import type { SurfaceDescriptor } from '@/components/surface-host/surface-host';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ROUTES } from '@/constants/routes';
import { workflowRunPath } from '@/constants/workflow-editor';
import { usePrimaryAgent } from '@/hooks/queries/agents/use-primary-agent';
import { useGetArtifact } from '@/hooks/queries/artifacts/use-get-artifact';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { useWorkflowCanvasSurfaceContext } from '@/hooks/use-workflow-canvas-surface-context';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { dispatchCanvasRun } from '@/stores/canvas-run-bridge-store';
import { dispatchCanvasApplyMutations } from '@/stores/canvas-apply-mutations-bridge-store';
import type { Agent } from '@/types/agent-types';
import type { ArtifactListItem } from '@/types/artifacts';
import type { ArtifactShareRole } from '@/types/share-types';
import { orgPath } from '@/utils/org-path';

import { ArtifactViewer } from '@/components/artifacts/artifact-viewer';

export function ArtifactViewerPage() {
  const { artifactId } = useParams<{ artifactId: string }>();
  const navigate = useOrgNavigate();
  const { isPrimaryChatEnabled } = useLaunchDarkly();
  // user_role arrives via router state from ArtifactCard, which already has it
  // from the list response. This is the only source until the detail endpoint
  // starts returning it. `search` carries the gallery's filters so Back
  // restores the list the user came from.
  const { state: routeState, search } = useLocation() as {
    state?: { user_role?: ArtifactShareRole };
    search: string;
  };
  // Resolving the agent is what decides between the two layouts below, so it
  // must not be conditional on it.
  const { data: primaryAgent } = usePrimaryAgent();

  const handleBack = useCallback(() => {
    navigate(`${ROUTES.ARTIFACTS}${search}`);
  }, [navigate, search]);

  const handleViewWorkflow = useCallback(
    (workflowId: string, runId?: string) => {
      navigate(workflowRunPath(workflowId, runId));
    },
    [navigate],
  );

  if (isPrimaryChatEnabled && primaryAgent && artifactId) {
    return (
      <ArtifactChatWorkspace
        agent={primaryAgent}
        routeArtifactId={artifactId}
        routeUserRole={routeState?.user_role}
        onBack={handleBack}
      />
    );
  }

  return (
    <ArtifactViewer
      artifactId={artifactId ?? ''}
      userRole={routeState?.user_role}
      rootClassName='h-svh'
      onViewWorkflow={handleViewWorkflow}
      onAfterDelete={handleBack}
      loadErrorAction={
        <Button variant='outline' onClick={handleBack}>
          Back to Artifacts
        </Button>
      }
      leading={
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='size-9 shrink-0'
                onClick={handleBack}
                aria-label='Back to Artifacts'
              >
                <ArrowLeft className='size-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='bottom'>Back to Artifacts</TooltipContent>
          </Tooltip>
        </>
      }
      onClose={handleBack}
    />
  );
}

function ArtifactChatWorkspace({
  agent,
  routeArtifactId,
  routeUserRole,
  onBack,
}: {
  agent: Agent;
  routeArtifactId: string;
  routeUserRole?: ArtifactShareRole;
  onBack: () => void;
}) {
  // Surface + chat-open state live in the URL. Chat defaults to collapsed:
  // arriving here from the gallery means you came to read.
  const {
    surface: urlSurface,
    chatOpen,
    setSurface,
    setChatOpen,
  } = useWorkspaceSurface({ chatOpenByDefault: false });

  // Resolve which conversation to resume for this artifact surface. The hook
  // checks the artifact's own conversation_id, then conversations that list
  // this artifact, then conversations that list its source workflow. Returns
  // null when there is nothing to resume, which opens a fresh thread.
  const artifactSurface = useMemo(
    () => ({ kind: 'artifact' as const, artifactId: routeArtifactId }),
    [routeArtifactId],
  );
  const {
    conversationId,
    conversationLinkResolving,
    onConversationChange,
    onTurnComplete,
    artifactLineage: {
      absorb: absorbArtifacts,
      keyFor: artifactContextKey,
      meta: artifactMeta,
    },
    closeLayeredSurface,
    renderRailHeader,
    isMountedCanvasLocked,
    applyMutationsDisabled,
    applyMutationsDisabledReason,
  } = useSurfaceChatHost({
    agentId: agent.id,
    resumeSurface: artifactSurface,
    setSurface,
  });

  // The surface follows the conversation, and the URL follows the surface —
  // but through history rather than the router. A pathname change remounts
  // the route subtree (RouterWithErrorBoundary keys on it), which would
  // destroy the very turn that produced the new artifact.
  const [shownArtifactId, setShownArtifactId] = useState(routeArtifactId);
  const showArtifact = useCallback((artifactId: string) => {
    setShownArtifactId((shown) => {
      if (shown === artifactId) return shown;
      window.history.replaceState(
        window.history.state,
        '',
        orgPath(ROUTES.ARTIFACT_DETAIL.replace(':artifactId', artifactId)),
      );
      return artifactId;
    });
  }, []);

  // Opened from a card inside the gallery pane: the gallery is a layered
  // surface, so it still wins over the artifact until it is explicitly
  // cleared -- otherwise the card click would silently update the hidden
  // anchor id while the gallery stays on screen.
  const openArtifactFromGallery = useCallback(
    (artifact: ArtifactListItem) => {
      showArtifact(artifact.id);
      setSurface(null);
    },
    [showArtifact, setSurface],
  );

  // A workflow opens in place, layered over the artifact as ?workflow=. The
  // path still names the artifact, which is what you return to when the
  // workflow closes — so this never leaves the page, and the conversation and
  // rail state carry across for free rather than being handed to another host.
  const openWorkflow = useCallback(
    (workflowId: string, runId?: string) => {
      setSurface({ kind: 'workflow', workflowId, runId });
    },
    [setSurface],
  );

  const openWorkflowArtifacts = useCallback(
    (workflowId: string) => {
      setSurface({ kind: 'artifact_gallery', workflowId, locked: true });
    },
    [setSurface],
  );

  // The artifact is this route's anchor, so it is the surface unless a
  // workflow or gallery is explicitly layered over it by a search param.
  const layeredSurface =
    urlSurface?.kind === 'workflow' || urlSurface?.kind === 'artifact_gallery'
      ? urlSurface
      : null;

  const surface: SurfaceDescriptor = layeredSurface ?? {
    kind: 'artifact',
    artifactId: shownArtifactId,
    // The role the gallery handed over describes the artifact we arrived on;
    // anything the conversation produced since is its own artifact, whose
    // access we have not been told.
    userRole: shownArtifactId === routeArtifactId ? routeUserRole : undefined,
  };

  // Ground the agent in whatever is actually on screen. A workflow beside the
  // chat needs the live graph, exactly as it would on chat-home.
  const workflowContext = useWorkflowCanvasSurfaceContext(
    layeredSurface?.kind === 'workflow' ? layeredSurface.workflowId : null,
    isMountedCanvasLocked,
  );
  // Shares a query key (and staleTime) with the viewer mounted beside the
  // chat, so this reads the artifact out of cache rather than fetching it
  // again. The title is what names the artifact in a context-switch marker.
  const { data: shownArtifact, isPending: shownArtifactPending } =
    useGetArtifact(shownArtifactId);
  const layeredWorkflowId =
    layeredSurface?.kind === 'workflow' ? layeredSurface.workflowId : null;

  const surfaceContext = useMemo((): SurfaceContext | null => {
    if (layeredSurface?.kind === 'workflow') {
      return workflowContext;
    }
    if (layeredSurface?.kind === 'artifact_gallery') {
      // The gallery is layered over the artifact, so the artifact underneath
      // is no longer what the user is looking at.
      return {
        surface: 'artifact_gallery',
        workflow_id: layeredSurface.workflowId,
      };
    }
    return buildArtifactSurfaceContext({
      artifactId: shownArtifactId,
      artifactContextKey,
      meta: artifactMeta.get(shownArtifactId),
      artifact: shownArtifact,
    });
  }, [
    layeredSurface,
    workflowContext,
    shownArtifactId,
    artifactContextKey,
    artifactMeta,
    shownArtifact,
  ]);

  return (
    <AgentChatProvider
      agentId={agent.id}
      conversationId={conversationId}
      conversationLinkResolving={conversationLinkResolving}
      onConversationChange={onConversationChange}
      onTurnComplete={onTurnComplete}
      surfaceContext={surfaceContext}
      // The canvas reports no surface while it loads (and a run reloads it);
      // this is what keeps that blink from reading as "nothing open".
      openWorkflowId={layeredWorkflowId}
      // Only when the artifact underneath is what the chat is about: a
      // workflow or a gallery layered over it resolves on its own, and
      // waiting on a fetch nobody is reading holds its marker back.
      surfaceResolving={!layeredSurface && shownArtifactPending}
      onOpenWorkflow={openWorkflow}
      onArtifact={(artifact) => {
        absorbArtifacts([artifact]);
        if (artifact.artifact_id) showArtifact(artifact.artifact_id);
      }}
      onArtifactHistory={absorbArtifacts}
      onOpenArtifact={showArtifact}
      onRunWorkflow={dispatchCanvasRun}
      onApplyMutations={dispatchCanvasApplyMutations}
      applyMutationsDisabled={applyMutationsDisabled}
      applyMutationsDisabledReason={applyMutationsDisabledReason}
    >
      <ChatWorkspace
        agentId={agent.id}
        surface={surface}
        // Closing a layered surface returns to the artifact the path names;
        // closing the artifact itself leaves for the gallery. Either way there
        // is somewhere to go, so the X is never withheld here.
        onCloseSurface={layeredSurface ? closeLayeredSurface : onBack}
        collapsed={!chatOpen}
        onCollapsedChange={(c) => setChatOpen(!c)}
        onViewWorkflowArtifacts={openWorkflowArtifacts}
        onOpenGalleryArtifact={openArtifactFromGallery}
        onGalleryChange={setSurface}
        renderRailHeader={renderRailHeader}
      />
    </AgentChatProvider>
  );
}
